import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { format, endOfMonth, eachDayOfInterval, getDay } from 'date-fns';
import { es } from 'date-fns/locale';

const COP = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(n) || 0);
const num = (v) => parseFloat(v) || 0;
const horasEntreTurnos = (inicio, fin) => {
  if (!inicio || !fin) return 0;
  const [hi, mi] = inicio.split(':').map(Number);
  const [hf, mf] = fin.split(':').map(Number);
  let diff = (hf * 60 + mf) - (hi * 60 + mi);
  if (diff < 0) diff += 24 * 60;
  return Math.round(diff / 60 * 100) / 100;
};

const DIAS = ['lunes','martes','miercoles','jueves','viernes','sabado','domingo'];
const DIAS_LABEL = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];
const DIAS_SEMANA_MAP = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];
const TIPO_CONTRATO = { termino_fijo: 'Término fijo', prestacion_servicios: 'Prestación de servicios', parcial: 'Parcial' };
const MESES_LABEL = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

// ─── HELPERS UI ───────────────────────────────────────────
function Row({ children }) { return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>; }
function Campo({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      {children}
    </div>
  );
}
const M = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  modal:   { background: 'white', borderRadius: 16, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.3)', overflow: 'hidden' },
  header:  { background: '#1a1a2e', color: 'white', padding: '18px 24px' },
  footer:  { display: 'flex', gap: 10, padding: '16px 24px', borderTop: '1px solid #F3F4F6', justifyContent: 'flex-end' },
  input:   { width: '100%', padding: '9px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  btnSec:  { padding: '10px 20px', border: '2px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151' },
  btnPri:  { padding: '10px 24px', border: 'none', borderRadius: 8, background: 'linear-gradient(135deg,#1a1a2e,#16213e)', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700 },
};

// ─── MODAL EMPLEADO ───────────────────────────────────────
function ModalEmpleado({ empleado, bodegas, onClose, onGuardado }) {
  const esEdicion = !!empleado?.id;
  const [form, setForm] = useState({
    nombre: '', cedula: '', cargo: '', bodega_id: '', tipo_contrato: 'termino_fijo',
    salario_base: '', valor_hora: '', fecha_ingreso: '', fecha_vencimiento: '',
    banco: '', numero_cuenta: '', tipo_cuenta: 'ahorros', activo: true,
    ...empleado,
  });
  const [guardando, setGuardando] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleGuardar = async () => {
    if (!form.nombre || !form.cedula) { toast.error('Nombre y cédula son obligatorios'); return; }
    setGuardando(true);
    try {
      if (esEdicion) await API.put(`/empleados/${empleado.id}`, form);
      else await API.post('/empleados', form);
      toast.success(esEdicion ? 'Empleado actualizado' : 'Empleado creado');
      onGuardado(); onClose();
    } catch (e) { toast.error(e.response?.data?.error || 'Error al guardar'); }
    finally { setGuardando(false); }
  };

  return (
    <div style={M.overlay}>
      <div style={{ ...M.modal, maxWidth: 560 }}>
        <div style={M.header}>
          <div style={{ fontSize: 13, opacity: 0.7 }}>{esEdicion ? 'Editar empleado' : 'Nuevo empleado'}</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>👤 {esEdicion ? form.nombre : 'Registrar empleado'}</div>
        </div>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14, maxHeight: '65vh', overflowY: 'auto' }}>
          <Row>
            <Campo label="Nombre completo *"><input value={form.nombre} onChange={set('nombre')} style={M.input} /></Campo>
            <Campo label="Cédula *"><input value={form.cedula} onChange={set('cedula')} style={M.input} /></Campo>
          </Row>
          <Row>
            <Campo label="Cargo"><input value={form.cargo} onChange={set('cargo')} style={M.input} /></Campo>
            <Campo label="Punto de venta">
              <select value={form.bodega_id} onChange={set('bodega_id')} style={M.input}>
                <option value="">— Sin asignar —</option>
                {bodegas.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
              </select>
            </Campo>
          </Row>
          <Row>
            <Campo label="Tipo de contrato">
              <select value={form.tipo_contrato} onChange={set('tipo_contrato')} style={M.input}>
                {Object.entries(TIPO_CONTRATO).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Campo>
            <Campo label="Fecha ingreso"><input type="date" value={form.fecha_ingreso || ''} onChange={set('fecha_ingreso')} style={M.input} /></Campo>
          </Row>
          <Row>
            <Campo label="Salario base"><input type="number" value={form.salario_base || ''} onChange={set('salario_base')} placeholder="Ej: 1423500" style={M.input} /></Campo>
            <Campo label="Valor hora (opcional)"><input type="number" value={form.valor_hora || ''} onChange={set('valor_hora')} placeholder="Auto si hay salario" style={M.input} /></Campo>
          </Row>
          {(form.tipo_contrato === 'termino_fijo' || form.tipo_contrato === 'parcial') && (
            <Campo label="Fecha vencimiento contrato">
              <input type="date" value={form.fecha_vencimiento || ''} onChange={set('fecha_vencimiento')} style={M.input} />
            </Campo>
          )}
          <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', marginBottom: 10 }}>Datos bancarios</div>
            <Row>
              <Campo label="Banco"><input value={form.banco || ''} onChange={set('banco')} placeholder="Ej: Bancolombia" style={M.input} /></Campo>
              <Campo label="Número de cuenta"><input value={form.numero_cuenta || ''} onChange={set('numero_cuenta')} style={M.input} /></Campo>
            </Row>
            <div style={{ marginTop: 12 }}>
              <Campo label="Tipo de cuenta">
                <select value={form.tipo_cuenta || 'ahorros'} onChange={set('tipo_cuenta')} style={M.input}>
                  <option value="ahorros">Ahorros</option>
                  <option value="corriente">Corriente</option>
                </select>
              </Campo>
            </div>
          </div>
        </div>
        <div style={M.footer}>
          <button onClick={onClose} style={M.btnSec}>Cancelar</button>
          <button onClick={handleGuardar} disabled={guardando} style={M.btnPri}>
            {guardando ? '⏳ Guardando...' : esEdicion ? '✓ Actualizar' : '+ Crear empleado'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MODAL HORARIO IDEAL ──────────────────────────────────
function ModalHorario({ empleado, onClose, onGuardado }) {
  const [form, setForm] = useState({
    empleado_id: empleado.id, nombre: '',
    lunes_inicio: '', lunes_fin: '',
    martes_inicio: '', martes_fin: '',
    miercoles_inicio: '', miercoles_fin: '',
    jueves_inicio: '', jueves_fin: '',
    viernes_inicio: '', viernes_fin: '',
    sabado_inicio: '', sabado_fin: '',
    domingo_inicio: '', domingo_fin: '',
    ...empleado.horario,
  });
  const [guardando, setGuardando] = useState(false);

  const setHora = (campo) => (e) => setForm(f => ({ ...f, [campo]: e.target.value }));

  const horasSemana = DIAS.reduce((total, dia) => {
    return total + horasEntreTurnos(form[`${dia}_inicio`], form[`${dia}_fin`]);
  }, 0);

  const colorHoras = horasSemana > 44 ? '#EF4444' : horasSemana >= 40 ? '#10B981' : horasSemana > 0 ? '#F59E0B' : '#9CA3AF';

  const handleGuardar = async () => {
    setGuardando(true);
    try {
      await API.post('/horarios', form);
      toast.success('Horario guardado');
      onGuardado(); onClose();
    } catch { toast.error('Error al guardar horario'); }
    finally { setGuardando(false); }
  };

  return (
    <div style={M.overlay}>
      <div style={{ ...M.modal, maxWidth: 560 }}>
        <div style={M.header}>
          <div style={{ fontSize: 13, opacity: 0.7 }}>🗓️ Horario semanal ideal</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>👤 {empleado.nombre}</div>
        </div>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '60vh', overflowY: 'auto' }}>
          <Campo label="Nombre del horario (opcional)">
            <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej: Horario estándar" style={M.input} />
          </Campo>

          {/* Resumen horas */}
          <div style={{ background: '#F9FAFB', borderRadius: 10, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#6B7280', fontWeight: 600 }}>Total horas semanales</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 20, fontWeight: 800, color: colorHoras, fontFamily: 'monospace' }}>{horasSemana.toFixed(1)}h</span>
              {horasSemana > 44 && <span style={{ fontSize: 11, background: '#FEE2E2', color: '#991B1B', padding: '2px 8px', borderRadius: 20, fontWeight: 700 }}>⚠️ Supera 44h</span>}
              {horasSemana >= 40 && horasSemana <= 44 && <span style={{ fontSize: 11, background: '#D1FAE5', color: '#065F46', padding: '2px 8px', borderRadius: 20, fontWeight: 700 }}>✅ OK</span>}
            </div>
          </div>

          {/* Tabla de días */}
          <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr 60px', background: '#F9FAFB', padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>
              <div>Día</div><div>Entrada</div><div>Salida</div><div>Horas</div>
            </div>
            {DIAS.map((dia, i) => {
              const horas = horasEntreTurnos(form[`${dia}_inicio`], form[`${dia}_fin`]);
              const esFinde = dia === 'sabado' || dia === 'domingo';
              return (
                <div key={dia} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr 60px', padding: '8px 12px', borderTop: '1px solid #F3F4F6', alignItems: 'center', background: esFinde ? '#FAFAFA' : 'white' }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: esFinde ? '#9CA3AF' : '#111827' }}>{DIAS_LABEL[i]}</div>
                  <input
                    type="time" value={form[`${dia}_inicio`] || ''}
                    onChange={setHora(`${dia}_inicio`)}
                    style={{ padding: '5px 8px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 13, outline: 'none', marginRight: 8 }}
                  />
                  <input
                    type="time" value={form[`${dia}_fin`] || ''}
                    onChange={setHora(`${dia}_fin`)}
                    style={{ padding: '5px 8px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 13, outline: 'none', marginRight: 8 }}
                  />
                  <div style={{ fontSize: 13, fontWeight: 700, color: horas > 0 ? '#374151' : '#D1D5DB', textAlign: 'right' }}>
                    {horas > 0 ? `${horas}h` : '—'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div style={M.footer}>
          <button onClick={onClose} style={M.btnSec}>Cancelar</button>
          <button onClick={handleGuardar} disabled={guardando} style={M.btnPri}>
            {guardando ? '⏳...' : '💾 Guardar horario'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MODAL TURNOS REALES ──────────────────────────────────
function ModalTurnos({ empleado, onClose }) {
  const hoy = new Date();
  const [mes, setMes] = useState(hoy.getMonth());
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [turnos, setTurnos] = useState({});
  const [guardando, setGuardando] = useState(false);

  const dias = eachDayOfInterval({ start: new Date(anio, mes, 1), end: new Date(anio, mes + 1, 0) });

  useEffect(() => {
    const inicio = format(new Date(anio, mes, 1), 'yyyy-MM-dd');
    const fin = format(new Date(anio, mes + 1, 0), 'yyyy-MM-dd');
    API.get('/turnos', { params: { empleado_id: empleado.id, inicio, fin } })
      .then(r => {
        const map = {};
        r.data.forEach(t => { map[t.fecha.slice(0, 10)] = t; });
        setTurnos(map);
      }).catch(() => {});
  }, [mes, anio]);

  const updateTurno = (fecha, campo, valor) => {
    setTurnos(prev => ({
      ...prev,
      [fecha]: { ...(prev[fecha] || {}), empleado_id: empleado.id, fecha, [campo]: valor }
    }));
  };

  const guardarDia = async (fecha) => {
    const t = turnos[fecha];
    if (!t) return;
    setGuardando(fecha);
    try {
      await API.post('/turnos', t);
      toast.success('Turno guardado');
    } catch { toast.error('Error al guardar'); }
    finally { setGuardando(false); }
  };

  const totalHoras = Object.values(turnos).reduce((a, t) => {
    if (t.ausente) return a;
    return a + num(t.horas_ordinarias) + num(t.horas_nocturnas) + num(t.horas_extra_diurnas) + num(t.horas_extra_nocturnas) + num(t.horas_festivo);
  }, 0);

  return (
    <div style={M.overlay}>
      <div style={{ ...M.modal, maxWidth: 700 }}>
        <div style={M.header}>
          <div style={{ fontSize: 13, opacity: 0.7 }}>📅 Turnos reales</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>👤 {empleado.nombre}</div>
        </div>
        <div style={{ padding: '12px 24px', borderBottom: '1px solid #F3F4F6', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={mes} onChange={e => setMes(Number(e.target.value))} style={{ ...M.input, width: 140 }}>
            {MESES_LABEL.map((m, i) => <option key={i} value={i}>{m}</option>)}
          </select>
          <select value={anio} onChange={e => setAnio(Number(e.target.value))} style={{ ...M.input, width: 90 }}>
            {[2025, 2026, 2027].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <div style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 700, color: '#374151' }}>
            Total mes: <span style={{ fontFamily: 'monospace', color: '#1a1a2e', fontSize: 15 }}>{totalHoras.toFixed(1)}h</span>
          </div>
        </div>
        <div style={{ padding: '16px 24px', maxHeight: '55vh', overflowY: 'auto' }}>
          {dias.map(dia => {
            const fechaStr = format(dia, 'yyyy-MM-dd');
            const diaSemana = getDay(dia);
            const t = turnos[fechaStr] || {};
            const totalDia = num(t.horas_ordinarias) + num(t.horas_nocturnas) + num(t.horas_extra_diurnas) + num(t.horas_extra_nocturnas) + num(t.horas_festivo);
            return (
              <div key={fechaStr} style={{ border: '1px solid #E5E7EB', borderRadius: 10, padding: '10px 14px', marginBottom: 8, background: t.ausente ? '#FEF2F2' : 'white' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: t.ausente ? 8 : 6 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>{DIAS_SEMANA_MAP[diaSemana]} {format(dia, 'd', { locale: es })}</span>
                    {totalDia > 0 && <span style={{ fontSize: 11, color: '#6B7280', fontFamily: 'monospace' }}>{totalDia.toFixed(1)}h</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label style={{ fontSize: 12, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!t.ausente} onChange={e => updateTurno(fechaStr, 'ausente', e.target.checked)} />
                      Ausente
                    </label>
                    <button onClick={() => guardarDia(fechaStr)} disabled={guardando === fechaStr} style={{ padding: '4px 10px', border: 'none', borderRadius: 6, background: '#1a1a2e', color: 'white', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                      {guardando === fechaStr ? '...' : '💾'}
                    </button>
                  </div>
                </div>
                {t.ausente ? (
                  <input value={t.motivo_ausencia || ''} onChange={e => updateTurno(fechaStr, 'motivo_ausencia', e.target.value)} placeholder="Motivo de ausencia..." style={{ ...M.input, fontSize: 12 }} />
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
                    {[
                      { campo: 'horas_ordinarias',    label: 'Ordinarias' },
                      { campo: 'horas_nocturnas',     label: 'Nocturnas' },
                      { campo: 'horas_extra_diurnas', label: 'Extra diurnas' },
                      { campo: 'horas_extra_nocturnas', label: 'Extra nocturnas' },
                      { campo: 'horas_festivo',       label: 'Festivo' },
                    ].map(({ campo, label }) => (
                      <div key={campo}>
                        <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 700, marginBottom: 2 }}>{label}</div>
                        <input
                          type="number" min="0" max="24" step="0.5"
                          value={t[campo] || ''}
                          onChange={e => updateTurno(fechaStr, campo, e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', outline: 'none' }}
                          placeholder="0"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={M.footer}>
          <button onClick={onClose} style={M.btnSec}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ─── MODAL PRÉSTAMO ───────────────────────────────────────
function ModalPrestamo({ empleado, onClose, onGuardado }) {
  const [form, setForm] = useState({ empleado_id: empleado.id, fecha: format(new Date(), 'yyyy-MM-dd'), tipo: 'adelanto', valor_total: '', cuotas: 1, descripcion: '' });
  const [guardando, setGuardando] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const valorCuota = form.valor_total && form.cuotas ? Math.ceil(form.valor_total / form.cuotas) : 0;

  const handleGuardar = async () => {
    if (!form.valor_total) { toast.error('Ingresa el valor'); return; }
    setGuardando(true);
    try {
      await API.post('/prestamos', form);
      toast.success('Préstamo registrado');
      onGuardado(); onClose();
    } catch { toast.error('Error al registrar'); }
    finally { setGuardando(false); }
  };

  return (
    <div style={M.overlay}>
      <div style={{ ...M.modal, maxWidth: 420 }}>
        <div style={M.header}>
          <div style={{ fontSize: 13, opacity: 0.7 }}>💳 Nuevo préstamo / adelanto</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>👤 {empleado.nombre}</div>
        </div>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Campo label="Tipo">
            <select value={form.tipo} onChange={set('tipo')} style={M.input}>
              <option value="adelanto">Adelanto de nómina</option>
              <option value="credito">Crédito sin interés</option>
            </select>
          </Campo>
          <Row>
            <Campo label="Valor total *"><input type="number" value={form.valor_total} onChange={set('valor_total')} placeholder="0" style={M.input} /></Campo>
            <Campo label="Cuotas"><input type="number" min="1" value={form.cuotas} onChange={set('cuotas')} style={M.input} /></Campo>
          </Row>
          {valorCuota > 0 && (
            <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#065F46', fontWeight: 600 }}>
              Descuento por quincena: <strong>{COP(valorCuota)}</strong>
            </div>
          )}
          <Campo label="Fecha"><input type="date" value={form.fecha} onChange={set('fecha')} style={M.input} /></Campo>
          <Campo label="Descripción">
            <textarea value={form.descripcion} onChange={set('descripcion')} rows={2} style={{ ...M.input, resize: 'none', fontFamily: 'inherit' }} placeholder="Motivo del préstamo..." />
          </Campo>
        </div>
        <div style={M.footer}>
          <button onClick={onClose} style={M.btnSec}>Cancelar</button>
          <button onClick={handleGuardar} disabled={guardando} style={M.btnPri}>
            {guardando ? '⏳...' : '+ Registrar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MODAL LIQUIDACIÓN ────────────────────────────────────
function ModalLiquidacion({ empleado, onClose }) {
  const hoy = new Date();
  const ini1 = format(new Date(hoy.getFullYear(), hoy.getMonth(), 1), 'yyyy-MM-dd');
  const fin1 = format(new Date(hoy.getFullYear(), hoy.getMonth(), 15), 'yyyy-MM-dd');
  const ini2 = format(new Date(hoy.getFullYear(), hoy.getMonth(), 16), 'yyyy-MM-dd');
  const fin2 = format(endOfMonth(hoy), 'yyyy-MM-dd');

  const [inicio, setInicio] = useState(hoy.getDate() <= 15 ? ini1 : ini2);
  const [fin, setFin]       = useState(hoy.getDate() <= 15 ? fin1 : fin2);
  const [resultado, setResultado] = useState(null);
  const [calculando, setCalculando] = useState(false);
  const [guardando, setGuardando]   = useState(false);
  const [descuadres, setDescuadres] = useState(0);
  const [otrosDesc, setOtrosDesc]   = useState(0);

  const calcular = async () => {
    setCalculando(true);
    try {
      const r = await API.post('/liquidaciones/calcular', { empleado_id: empleado.id, periodo_inicio: inicio, periodo_fin: fin });
      setResultado(r.data);
    } catch { toast.error('Error al calcular'); }
    finally { setCalculando(false); }
  };

  const guardar = async () => {
    if (!resultado) return;
    setGuardando(true);
    const totalDesc = num(resultado.descuentos.totalDescuentos) + num(descuadres) + num(otrosDesc);
    const neto = num(resultado.valores.subtotalDevengado) - totalDesc;
    try {
      await API.post('/liquidaciones', {
        empleado_id: empleado.id,
        periodo_inicio: inicio, periodo_fin: fin,
        salario_base: empleado.salario_base,
        valor_ordinario: resultado.valores.valorOrdinario,
        valor_nocturno: resultado.valores.valorNocturno,
        valor_extra_diurno: resultado.valores.valorExtraDiurno,
        valor_extra_nocturno: resultado.valores.valorExtraNocturno,
        valor_dominical: resultado.valores.valorFestivo,
        subtotal_devengado: resultado.valores.subtotalDevengado,
        descuento_ausencias: resultado.descuentos.descuentoAusencias,
        descuento_prestamos: resultado.descuentos.descuentoPrestamos,
        descuento_descuadres: descuadres,
        otros_descuentos: otrosDesc,
        total_descuentos: totalDesc,
        neto_a_pagar: neto,
      });
      toast.success('Liquidación guardada');
      onClose();
    } catch { toast.error('Error al guardar'); }
    finally { setGuardando(false); }
  };

  const totalDesc = resultado ? num(resultado.descuentos.totalDescuentos) + num(descuadres) + num(otrosDesc) : 0;
  const neto = resultado ? num(resultado.valores.subtotalDevengado) - totalDesc : 0;

  return (
    <div style={M.overlay}>
      <div style={{ ...M.modal, maxWidth: 520 }}>
        <div style={M.header}>
          <div style={{ fontSize: 13, opacity: 0.7 }}>💰 Liquidación quincenal</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>👤 {empleado.nombre}</div>
        </div>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14, maxHeight: '65vh', overflowY: 'auto' }}>
          <Row>
            <Campo label="Inicio período"><input type="date" value={inicio} onChange={e => setInicio(e.target.value)} style={M.input} /></Campo>
            <Campo label="Fin período"><input type="date" value={fin} onChange={e => setFin(e.target.value)} style={M.input} /></Campo>
          </Row>
          <button onClick={calcular} disabled={calculando} style={{ ...M.btnPri, width: '100%' }}>
            {calculando ? '⏳ Calculando...' : '🔢 Calcular liquidación'}
          </button>

          {resultado && (
            <>
              <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontWeight: 800, fontSize: 13, color: '#065F46', marginBottom: 10 }}>📈 DEVENGADO</div>
                {[
                  { label: `Ordinarias (${resultado.horas.totalOrdinarias}h)`, valor: resultado.valores.valorOrdinario },
                  { label: `Nocturnas (${resultado.horas.totalNocturnas}h)`, valor: resultado.valores.valorNocturno },
                  { label: `Extra diurnas (${resultado.horas.totalExtraDiurnas}h)`, valor: resultado.valores.valorExtraDiurno },
                  { label: `Extra nocturnas (${resultado.horas.totalExtraNocturnas}h)`, valor: resultado.valores.valorExtraNocturno },
                  { label: `Festivos (${resultado.horas.totalFestivo}h)`, valor: resultado.valores.valorFestivo },
                ].filter(x => num(x.valor) > 0).map((x, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <span style={{ color: '#374151' }}>{x.label}</span>
                    <span style={{ fontWeight: 700, fontFamily: 'monospace' }}>{COP(x.valor)}</span>
                  </div>
                ))}
                <div style={{ borderTop: '1px solid #BBF7D0', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 14, color: '#065F46' }}>
                  <span>Total devengado</span>
                  <span style={{ fontFamily: 'monospace' }}>{COP(resultado.valores.subtotalDevengado)}</span>
                </div>
              </div>

              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontWeight: 800, fontSize: 13, color: '#991B1B', marginBottom: 10 }}>📉 DESCUENTOS</div>
                {num(resultado.descuentos.descuentoAusencias) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <span>Ausencias ({resultado.horas.diasAusente} días)</span>
                    <span style={{ fontWeight: 700, fontFamily: 'monospace', color: '#EF4444' }}>− {COP(resultado.descuentos.descuentoAusencias)}</span>
                  </div>
                )}
                {num(resultado.descuentos.descuentoPrestamos) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <span>Préstamos / adelantos</span>
                    <span style={{ fontWeight: 700, fontFamily: 'monospace', color: '#EF4444' }}>− {COP(resultado.descuentos.descuentoPrestamos)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4, alignItems: 'center' }}>
                  <span>Descuadres de caja</span>
                  <input type="number" value={descuadres} onChange={e => setDescuadres(e.target.value)} style={{ width: 110, padding: '4px 8px', border: '1px solid #FECACA', borderRadius: 6, fontSize: 12, textAlign: 'right' }} placeholder="0" />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4, alignItems: 'center' }}>
                  <span>Otros descuentos</span>
                  <input type="number" value={otrosDesc} onChange={e => setOtrosDesc(e.target.value)} style={{ width: 110, padding: '4px 8px', border: '1px solid #FECACA', borderRadius: 6, fontSize: 12, textAlign: 'right' }} placeholder="0" />
                </div>
                <div style={{ borderTop: '1px solid #FECACA', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 14, color: '#991B1B' }}>
                  <span>Total descuentos</span>
                  <span style={{ fontFamily: 'monospace' }}>− {COP(totalDesc)}</span>
                </div>
              </div>

              <div style={{ background: '#1a1a2e', borderRadius: 10, padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: 'white', fontWeight: 800, fontSize: 16 }}>💵 NETO A PAGAR</span>
                <span style={{ color: '#10B981', fontWeight: 800, fontSize: 22, fontFamily: 'monospace' }}>{COP(neto)}</span>
              </div>
            </>
          )}
        </div>
        <div style={M.footer}>
          <button onClick={onClose} style={M.btnSec}>Cerrar</button>
          {resultado && (
            <button onClick={guardar} disabled={guardando} style={M.btnPri}>
              {guardando ? '⏳...' : '💾 Guardar liquidación'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── TAB HORARIOS ─────────────────────────────────────────
function TabHorarios({ empleados, onEditarHorario }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {empleados.map(emp => {
        const h = emp.horario;
        const horasSemana = h ? DIAS.reduce((t, dia) => t + horasEntreTurnos(h[`${dia}_inicio`], h[`${dia}_fin`]), 0) : 0;
        const colorH = horasSemana > 44 ? '#EF4444' : horasSemana >= 40 ? '#10B981' : horasSemana > 0 ? '#F59E0B' : '#9CA3AF';
        return (
          <div key={emp.id} style={{ background: 'white', borderRadius: 14, padding: '14px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: h ? 12 : 0, flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15 }}>{emp.nombre}</div>
                <div style={{ fontSize: 12, color: '#6B7280' }}>{emp.cargo || 'Sin cargo'} · {emp.bodega_nombre || 'Sin punto'}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {horasSemana > 0 && (
                  <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 16, color: colorH }}>{horasSemana.toFixed(1)}h/sem</span>
                )}
                <button onClick={() => onEditarHorario(emp)} style={{ padding: '7px 14px', border: '2px solid #1a1a2e', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                  {h ? '✏️ Editar' : '+ Asignar horario'}
                </button>
              </div>
            </div>
            {h && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                {DIAS.map((dia, i) => {
                  const horas = horasEntreTurnos(h[`${dia}_inicio`], h[`${dia}_fin`]);
                  return (
                    <div key={dia} style={{ background: horas > 0 ? '#F0FDF4' : '#F9FAFB', borderRadius: 6, padding: '6px 4px', textAlign: 'center' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', marginBottom: 2 }}>{DIAS_LABEL[i]}</div>
                      {horas > 0 ? (
                        <>
                          <div style={{ fontSize: 10, color: '#374151' }}>{h[`${dia}_inicio`]?.slice(0,5)}</div>
                          <div style={{ fontSize: 10, color: '#374151' }}>{h[`${dia}_fin`]?.slice(0,5)}</div>
                          <div style={{ fontSize: 11, fontWeight: 800, color: '#065F46', marginTop: 2 }}>{horas}h</div>
                        </>
                      ) : (
                        <div style={{ fontSize: 11, color: '#D1D5DB' }}>—</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── TABLA LIQUIDACIONES ──────────────────────────────────
function TablaLiquidaciones() {
  const hoy = new Date();
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    const inicio = format(new Date(hoy.getFullYear(), hoy.getMonth(), 1), 'yyyy-MM-dd');
    const fin = format(endOfMonth(hoy), 'yyyy-MM-dd');
    API.get('/liquidaciones', { params: { inicio, fin } })
      .then(r => setLiquidaciones(r.data))
      .catch(() => {})
      .finally(() => setCargando(false));
  }, []);

  if (cargando) return <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Cargando...</div>;
  if (!liquidaciones.length) return (
    <div style={{ background: 'white', borderRadius: 14, padding: 40, textAlign: 'center', color: '#9CA3AF', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
      <div style={{ fontSize: 15, fontWeight: 600 }}>Sin liquidaciones este mes</div>
    </div>
  );

  return (
    <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              {['Empleado','Período','Devengado','Descuentos','Neto a pagar','Estado'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {liquidaciones.map((l, i) => (
              <tr key={i} style={{ borderTop: '1px solid #F3F4F6', background: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                <td style={{ padding: '10px 14px', fontWeight: 700 }}>{l.empleado_nombre}</td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: '#6B7280' }}>
                  {format(new Date(l.periodo_inicio), 'd MMM')} — {format(new Date(l.periodo_fin), 'd MMM yyyy')}
                </td>
                <td style={{ padding: '10px 14px', fontFamily: 'monospace', color: '#10B981', fontWeight: 700 }}>{COP(l.subtotal_devengado)}</td>
                <td style={{ padding: '10px 14px', fontFamily: 'monospace', color: '#EF4444', fontWeight: 700 }}>− {COP(l.total_descuentos)}</td>
                <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontWeight: 800, fontSize: 15, color: '#1a1a2e' }}>{COP(l.neto_a_pagar)}</td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: l.estado === 'pagada' ? '#D1FAE5' : l.estado === 'aprobada' ? '#DBEAFE' : '#F3F4F6', color: l.estado === 'pagada' ? '#065F46' : l.estado === 'aprobada' ? '#1E40AF' : '#6B7280' }}>
                    {l.estado}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── MÓDULO PRINCIPAL ─────────────────────────────────────
export default function Nomina() {
  const { puedeVer } = useAuth();
  const esAdmin = puedeVer('gerencia', 'administracion');
  const hoy = new Date();

  const [empleados, setEmpleados] = useState([]);
  const [bodegas, setBodegas]     = useState([]);
  const [cargando, setCargando]   = useState(true);
  const [tab, setTab]             = useState('empleados');
  const [busqueda, setBusqueda]   = useState('');

  const [modalEmpleado,   setModalEmpleado]   = useState(null);
  const [modalTurnos,     setModalTurnos]     = useState(null);
  const [modalPrestamo,   setModalPrestamo]   = useState(null);
  const [modalLiquidacion,setModalLiquidacion]= useState(null);
  const [modalHorario,    setModalHorario]    = useState(null);

  const cargar = () => {
    setCargando(true);
    Promise.all([
      API.get('/empleados'),
      API.get('/bodegas').catch(() => ({ data: [] })),
    ]).then(([e, b]) => {
      setEmpleados(e.data);
      setBodegas(b.data);
    }).catch(() => toast.error('Error cargando datos'))
    .finally(() => setCargando(false));
  };

  useEffect(() => { cargar(); }, []);

  const empleadosFiltrados = empleados.filter(e =>
    e.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    e.cedula.includes(busqueda)
  );

  const porVencer = empleados.filter(e => {
    if (!e.fecha_vencimiento) return false;
    const dias = Math.ceil((new Date(e.fecha_vencimiento) - hoy) / (1000 * 60 * 60 * 24));
    return dias >= 0 && dias <= 30;
  });

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#111827' }}>👥 Nómina</h1>
          <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 14 }}>{empleados.length} empleados activos</p>
        </div>
        {esAdmin && (
          <button onClick={() => setModalEmpleado({})} style={{ ...M.btnPri, padding: '10px 20px' }}>
            + Nuevo empleado
          </button>
        )}
      </div>

      {porVencer.length > 0 && (
        <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 12, padding: '12px 18px', marginBottom: 20 }}>
          <div style={{ fontWeight: 800, color: '#92400E', fontSize: 14 }}>
            ⚠️ {porVencer.length} contrato{porVencer.length > 1 ? 's' : ''} por vencer en los próximos 30 días
          </div>
          <div style={{ fontSize: 13, color: '#B45309', marginTop: 4 }}>
            {porVencer.map(e => `${e.nombre} (${format(new Date(e.fecha_vencimiento + 'T12:00'), 'd MMM yyyy')})`).join(' · ')}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'white', borderRadius: 12, padding: 4, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', width: 'fit-content' }}>
        {[
          { id: 'empleados',    label: '👤 Empleados' },
          { id: 'liquidaciones',label: '💰 Liquidaciones' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: '8px 18px', border: 'none', borderRadius: 8, background: tab === t.id ? '#1a1a2e' : 'transparent', color: tab === t.id ? 'white' : '#6B7280', cursor: 'pointer', fontSize: 13, fontWeight: tab === t.id ? 700 : 500 }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'empleados' && (
        <>
          <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="🔍 Buscar por nombre o cédula..." style={{ ...M.input, marginBottom: 16, maxWidth: 340 }} />
          {cargando ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Cargando...</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {empleadosFiltrados.map(emp => {
                const diasVence = emp.fecha_vencimiento
                  ? Math.ceil((new Date(emp.fecha_vencimiento + 'T12:00') - hoy) / (1000 * 60 * 60 * 24))
                  : null;
                return (
                  <div key={emp.id} style={{ background: 'white', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#1a1a2e', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, flexShrink: 0 }}>
                          {emp.nombre.charAt(0)}
                        </div>
                        <div>
                          <div style={{ fontWeight: 800, fontSize: 15 }}>{emp.nombre}</div>
                          <div style={{ fontSize: 12, color: '#6B7280' }}>CC {emp.cedula} · {emp.cargo || 'Sin cargo'} · {emp.bodega_nombre || 'Sin punto'}</div>
                        </div>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#EEF2FF', color: '#4338CA', fontWeight: 700 }}>
                          {TIPO_CONTRATO[emp.tipo_contrato]}
                        </span>
                        {diasVence !== null && diasVence <= 30 && (
                          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#FEF3C7', color: '#92400E', fontWeight: 700 }}>
                            ⚠️ Vence en {diasVence} días
                          </span>
                        )}
                      </div>
                      <div style={{ marginTop: 8, fontSize: 13, color: '#374151', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        {emp.salario_base && <span>💵 {COP(emp.salario_base)}</span>}
                        {emp.banco && <span>🏦 {emp.banco} · {emp.numero_cuenta}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button onClick={() => setModalTurnos(emp)}      style={{ ...btnAcc, background: '#EEF2FF', color: '#4338CA' }}>📅 Turnos</button>
                      <button onClick={() => setModalPrestamo(emp)}    style={{ ...btnAcc, background: '#FEF3C7', color: '#92400E' }}>💳 Préstamo</button>
                      <button onClick={() => setModalLiquidacion(emp)} style={{ ...btnAcc, background: '#D1FAE5', color: '#065F46' }}>💰 Liquidar</button>
                      {esAdmin && <button onClick={() => setModalEmpleado(emp)} style={{ ...btnAcc, background: '#F3F4F6', color: '#374151' }}>✏️</button>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {tab === 'liquidaciones' && <TablaLiquidaciones />}

      {modalEmpleado    !== null && <ModalEmpleado    empleado={modalEmpleado}    bodegas={bodegas} onClose={() => setModalEmpleado(null)}    onGuardado={cargar} />}
      {modalTurnos      && <ModalTurnos      empleado={modalTurnos}      onClose={() => setModalTurnos(null)} />}
      {modalPrestamo    && <ModalPrestamo    empleado={modalPrestamo}    onClose={() => setModalPrestamo(null)}    onGuardado={cargar} />}
      {modalLiquidacion && <ModalLiquidacion empleado={modalLiquidacion} onClose={() => setModalLiquidacion(null)} />}
    </div>
  );
}

const btnAcc = { padding: '6px 12px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 };
